import { create } from 'apisauce';
import { toast } from 'react-toastify';
import axios from 'axios';
import { SET_ACTIVE_APP } from '../../store/constants/auth';
import { store } from '../../store';
import { userLogOut } from '../../store/actions/auth';

export const DEVICE_CHANGE =
  'Your session has expire or you have log in on another device please logout and login again';

const apiUrl =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000'
    : process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const apiMain = create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const api = create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.addRequestTransform((request) => {
  if (process.env.NODE_ENV === 'development') {
    request.baseURL = 'http://localhost:8080';
  }

  let token = store.getState().auth?.token;
  if (!token) {
    try {
      const userSession = localStorage.getItem('user_session');
      if (userSession) {
        token = JSON.parse(userSession)?.token;
      }
    } catch (e) {
      // ignore
    }
  }
  if (token && (!request.headers.Authorization || !request.headers.authorization)) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
  if (bootstrappedApp) {
    const app = JSON.parse(bootstrappedApp);
    if (process.env.NODE_ENV !== 'development') {
      request.baseURL = app.urls.apiUrl;
    }
    request.headers['x-app-id'] = app._id;
    return;
  }

  const activeApp = localStorage.getItem('activeApp');
  if (activeApp) {
    const app = JSON.parse(activeApp);
    store.dispatch({ type: SET_ACTIVE_APP, payload: app });
    if (process.env.NODE_ENV !== 'development') {
      request.baseURL = app.urls.apiUrl;
    }
    request.headers['x-app-id'] = app._id;
  }
});

apiMain.addRequestTransform((request) => {
  if (process.env.NODE_ENV === 'development') {
    request.baseURL = 'http://localhost:8000';
  }

  let token = store.getState().auth?.token;
  if (!token) {
    try {
      const userSession = localStorage.getItem('user_session');
      if (userSession) {
        token = JSON.parse(userSession)?.token;
      }
    } catch (e) {
      // ignore
    }
  }
  if (token && (!request.headers.Authorization || !request.headers.authorization)) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
  if (bootstrappedApp) {
    const app = JSON.parse(bootstrappedApp);
    request.headers['x-app-id'] = app._id;
    return;
  }

  const activeApp = localStorage.getItem('activeApp');
  if (activeApp) {
    const app = JSON.parse(activeApp);
    request.headers['x-app-id'] = app._id;
  }
});

// Interceptor logic for automatic token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const setupInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const isLoginPage = window.location.pathname === '/login';

      if (error.response?.status === 401 && !originalRequest._retry && !isLoginPage) {
        if (originalRequest.url.includes('/refresh')) {
          store.dispatch(userLogOut());
          window.location.href = '/login';
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          store.dispatch(userLogOut());
          window.location.href = '/login';
          isRefreshing = false;
          return Promise.reject(error);
        }

        try {
          const res = await axios.post(`${apiUrl}/api/v1/user/refresh`, {
            refreshToken,
          });

          if (res.data?.success) {
            const { authorization, refreshToken: newRefreshToken } = res.data.data;
            const newToken = authorization.token;

            localStorage.setItem('refresh_token', newRefreshToken);
            const userSession = localStorage.getItem('user_session')
              ? JSON.parse(localStorage.getItem('user_session'))
              : {};
            userSession.token = newToken;
            localStorage.setItem('user_session', JSON.stringify(userSession));

            store.dispatch({
              type: 'LOGIN_SUCCEED',
              payload: userSession,
            });

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          store.dispatch(userLogOut());
          window.location.href = '/login';
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      }

      if (error.response?.status === 409 && error.response?.data?.message === DEVICE_CHANGE && !isLoginPage) {
        store.dispatch(userLogOut());
        window.location.href = '/login';
        toast.info('Your session has expired please login again');
      }

      if (error.response?.status === 403 && error.response?.data?.code === 'APP_ACCESS_REVOKED') {
        toast.error('Your access to this application has been revoked.');
        const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
        if (bootstrappedApp) {
          store.dispatch(userLogOut());
          window.location.href = '/login';
        } else {
          localStorage.removeItem('activeApp');
          store.dispatch({ type: SET_ACTIVE_APP, payload: null });
          window.location.href = '/';
        }
      }

      return Promise.reject(error);
    }
  );
};

setupInterceptor(apiMain.axiosInstance);
setupInterceptor(api.axiosInstance);
